/*
 * ---------------------------------------------------------------
 * ## HAND-WRITTEN STAND-IN FOR A SWAGGER-GENERATED CLIENT       ##
 * ##                                                           ##
 * ## The Drive routes (DriveController, PlanDriveController)    ##
 * ## are not on a deployed backend yet, so `yarn generate:api-*`##
 * ## cannot emit them. This class mirrors the generated shape   ##
 * ## (extends HttpClient, `this.request`, `secure: true`) and   ##
 * ## lives outside `src/api/` only because that folder is       ##
 * ## git-ignored and wiped on every regeneration. Once the      ##
 * ## backend ships, re-point the import at the generated class. ##
 * ---------------------------------------------------------------
 */
import { ContentType, HttpClient, RequestParams } from '@/api/http-client';

import { DriveFolderRef, driveFolderPath } from '../model/driveFolders';
import {
  DriveAccessRow,
  DriveCopyRequest,
  DriveCopyResult,
  DrivePreviewAccepted,
  DrivePreviewRequest,
  DriveRefreshResult,
  DriveShareRequest,
  DriveUploadIntent,
  OrderDriveSummary,
  PlanAccessRow,
  PlanDriveSummary,
} from '../model/types';

const orderDrive = (orderId: string) => `/orders/${encodeURIComponent(orderId)}/drive`;

const folderRoute = (orderId: string, folder: DriveFolderRef) =>
  `${orderDrive(orderId)}/folders/${driveFolderPath(folder)}`;

const planDrive = (planId: string) => `/mediaPlans/${encodeURIComponent(planId)}/drive`;

export class ArtworkDrive<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
  /** @request GET:/orders/{orderId}/drive — counts, "Last synced" and links over the caller's bookings. */
  getOrderDriveSummary = (orderId: string, params: RequestParams = {}) =>
    this.request<OrderDriveSummary, void>({
      path: orderDrive(orderId),
      method: 'GET',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request GET:/orders/{orderId}/drive/folders/{kind}/{id}/access */
  listFolderAccess = (orderId: string, folder: DriveFolderRef, params: RequestParams = {}) =>
    this.request<DriveAccessRow[], void>({
      path: `${folderRoute(orderId, folder)}/access`,
      method: 'GET',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request POST:/orders/{orderId}/drive/folders/{kind}/{id}/access */
  shareFolder = (
    orderId: string,
    folder: DriveFolderRef,
    data: DriveShareRequest,
    params: RequestParams = {},
  ) =>
    this.request<DriveAccessRow, void>({
      path: `${folderRoute(orderId, folder)}/access`,
      method: 'POST',
      body: data,
      secure: true,
      type: ContentType.Json,
      format: 'json',
      ...params,
    });

  /** @request DELETE:/orders/{orderId}/drive/folders/{kind}/{id}/access/{permissionId} */
  unshareFolder = (
    orderId: string,
    folder: DriveFolderRef,
    permissionId: string,
    params: RequestParams = {},
  ) =>
    this.request<void, void>({
      path: `${folderRoute(orderId, folder)}/access/${encodeURIComponent(permissionId)}`,
      method: 'DELETE',
      secure: true,
      ...params,
    });

  /** @request POST:/orders/{orderId}/drive/folders/{kind}/{id}/upload-intent */
  uploadIntent = (orderId: string, folder: DriveFolderRef, params: RequestParams = {}) =>
    this.request<DriveUploadIntent, void>({
      path: `${folderRoute(orderId, folder)}/upload-intent`,
      method: 'POST',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request POST:/orders/{orderId}/drive/folders/{kind}/{id}/refresh */
  refreshFolder = (orderId: string, folder: DriveFolderRef, params: RequestParams = {}) =>
    this.request<DriveRefreshResult, void>({
      path: `${folderRoute(orderId, folder)}/refresh`,
      method: 'POST',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request POST:/orders/{orderId}/drive/copy — staff only. */
  copyToDecoration = (orderId: string, data: DriveCopyRequest, params: RequestParams = {}) =>
    this.request<DriveCopyResult, void>({
      path: `${orderDrive(orderId)}/copy`,
      method: 'POST',
      body: data,
      secure: true,
      type: ContentType.Json,
      format: 'json',
      ...params,
    });

  /** @request POST:/orders/{orderId}/drive/folders/decoration/{operationId}/previews — 202 once queued. */
  generatePreviews = (
    orderId: string,
    operationId: string,
    data: DrivePreviewRequest,
    params: RequestParams = {},
  ) =>
    this.request<DrivePreviewAccepted, void>({
      path: `${folderRoute(orderId, { kind: 'decoration', operationId })}/previews`,
      method: 'POST',
      body: data,
      secure: true,
      type: ContentType.Json,
      format: 'json',
      ...params,
    });

  /** @request GET:/mediaPlans/{planId}/drive — each line's folder, listed live from Drive. */
  getPlanDriveSummary = (planId: string, params: RequestParams = {}) =>
    this.request<PlanDriveSummary, void>({
      path: planDrive(planId),
      method: 'GET',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request GET:/mediaPlans/{planId}/drive/access */
  listPlanAccess = (planId: string, params: RequestParams = {}) =>
    this.request<PlanAccessRow[], void>({
      path: `${planDrive(planId)}/access`,
      method: 'GET',
      secure: true,
      format: 'json',
      ...params,
    });

  /** @request POST:/mediaPlans/{planId}/drive/access — the author and staff only. */
  sharePlan = (planId: string, data: DriveShareRequest, params: RequestParams = {}) =>
    this.request<PlanAccessRow, void>({
      path: `${planDrive(planId)}/access`,
      method: 'POST',
      body: data,
      secure: true,
      type: ContentType.Json,
      format: 'json',
      ...params,
    });

  /** @request DELETE:/mediaPlans/{planId}/drive/access/{permissionId} */
  unsharePlan = (planId: string, permissionId: string, params: RequestParams = {}) =>
    this.request<void, void>({
      path: `${planDrive(planId)}/access/${encodeURIComponent(permissionId)}`,
      method: 'DELETE',
      secure: true,
      ...params,
    });
}
